import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Search, X, User, MapPin, Heart, Briefcase, BookOpen, Eye, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { MembershipBadge, DocumentVerifiedBadge } from "../components/MembershipBadge";
import { safeFetch } from "../utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_TAGS = [
  { label: "50대", value: "50대" },
  { label: "60대", value: "60대" },
  { label: "서울", value: "서울" },
  { label: "토론토", value: "토론토" },
  { label: "기독교", value: "기독교" },
  { label: "불교", value: "불교" },
  { label: "무교", value: "무교" },
  { label: "이혼", value: "이혼" },
  { label: "사별", value: "사별" },
  { label: "ENFP", value: "ENFP" },
  { label: "INFJ", value: "INFJ" },
  { label: "등산", value: "등산" },
  { label: "골프", value: "골프" },
  { label: "여행", value: "여행" },
  { label: "요리", value: "요리" },
];

export default function AdminMemberSearchPage() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading } = useAuth();
  const [keywords, setKeywords] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [results, setResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [introText, setIntroText] = useState("");
  const [aiKeywords, setAiKeywords] = useState([]);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  if (!loading && (!isAuthenticated || user?.role !== "admin")) {
    navigate("/");
    return null;
  }

  const addKeyword = (kw) => {
    const trimmed = kw.trim().replace(/^#+/, "");
    if (trimmed && !keywords.includes(trimmed) && keywords.length < 5) {
      setKeywords([...keywords, trimmed]);
      setInputValue("");
    }
  };

  const parseKeywords = (text) => {
    return text
      .split(/[,\s]+/)
      .map(s => s.trim().replace(/^#+/, ""))
      .filter(Boolean);
  };

  const commitInput = () => {
    const parsed = parseKeywords(inputValue);
    if (parsed.length === 0) return keywords;
    const merged = [...keywords];
    for (const kw of parsed) {
      if (!merged.includes(kw) && merged.length < 5) merged.push(kw);
    }
    setKeywords(merged);
    setInputValue("");
    return merged;
  };

  const removeKeyword = (kw) => setKeywords(keywords.filter(k => k !== kw));

  const handleSearch = async () => {
    // Auto-commit any pending input text as keywords before searching
    const effectiveKeywords = inputValue.trim() ? commitInput() : keywords;
    if (effectiveKeywords.length === 0) return;
    setIsSearching(true);
    setSelectedMember(null);
    try {
      const res = await safeFetch(`${API_URL}/api/admin/members/search?q=${encodeURIComponent(effectiveKeywords.join(","))}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setResults(data.members || []);
      } else {
        // Still show "no results" UI on non-OK response instead of initial state
        setResults([]);
      }
    } catch {
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === ",") && inputValue.trim()) {
      e.preventDefault();
      commitInput();
    } else if (e.key === "Backspace" && !inputValue && keywords.length > 0) {
      // Convenience: remove last chip with backspace on empty input
      setKeywords(keywords.slice(0, -1));
    }
  };

  const getAge = (birthYear) => birthYear ? new Date().getFullYear() - birthYear : null;

  const handleAiRecommend = async () => {
    const intro = introText.trim();
    if (intro.length < 5) {
      setAiError("자기소개서 내용을 입력해주세요.");
      return;
    }
    setIsAiLoading(true);
    setAiError("");
    setSelectedMember(null);
    setAiKeywords([]);
    try {
      const res = await safeFetch(`${API_URL}/api/admin/members/ai-recommend`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ keywords: intro }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "AI 분석에 실패했습니다.");
      setAiKeywords(data.keywords || []);
      setKeywords(data.keywords || []);
      setResults(data.members || []);
    } catch (e) {
      setAiError(e.message || "AI 분석에 실패했습니다.");
    } finally {
      setIsAiLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 pt-20" data-testid="admin-member-search-page">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate("/admin")} className="p-2 hover:bg-stone-200 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-stone-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-stone-800">회원 찾기</h1>
            <p className="text-sm text-stone-500">키워드를 입력하여 조건에 맞는 회원을 검색하세요 (최대 5개)</p>
          </div>
        </div>

        {/* Search Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-6">
          {/* Keyword Input */}
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1 flex items-center flex-wrap gap-2 bg-stone-50 border border-stone-200 rounded-xl px-3 py-2.5 min-h-[44px]">
              {keywords.map((kw) => (
                <span key={kw} className="flex items-center gap-1 bg-burgundy-100 text-burgundy-700 px-2.5 py-1 rounded-full text-xs font-medium">
                  {kw}
                  <button onClick={() => removeKeyword(kw)} className="hover:text-burgundy-900"><X className="w-3 h-3" /></button>
                </span>
              ))}
              {keywords.length < 5 && (
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={keywords.length === 0 ? "예: 50대, 서울, 기독교 (쉼표·공백·Enter로 구분, 최대 5개)" : "추가 키워드..."}
                  className="flex-1 min-w-[120px] bg-transparent text-sm outline-none"
                  data-testid="member-search-input"
                />
              )}
            </div>
            <Button onClick={handleSearch} disabled={isSearching || (keywords.length === 0 && !inputValue.trim())} className="btn-primary flex items-center gap-2 px-6">
              <Search className="w-4 h-4" />
              검색
            </Button>
          </div>

          {/* Quick Tags */}
          <div className="flex flex-wrap gap-1.5">
            {QUICK_TAGS.map((tag) => (
              <button
                key={tag.value}
                onClick={() => addKeyword(tag.value)}
                disabled={keywords.includes(tag.value) || keywords.length >= 5}
                className="px-2.5 py-1 text-xs bg-stone-100 hover:bg-stone-200 text-stone-600 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {tag.label}
              </button>
            ))}
          </div>
        </div>

        {/* AI 자기소개서 기반 추천 */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-6" data-testid="ai-recommend-section">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-stone-800 text-white">
              <Sparkles className="w-4 h-4" />
            </span>
            <h2 className="text-base font-bold text-stone-800">AI 자기소개서 검색 추천</h2>
          </div>
          <p className="text-xs text-stone-500 mb-3">찾으시는 회원의 키워드나 조건을 입력하면, AI가 회원들의 프로필 자기소개서를 검색해 가장 잘 맞는 분을 추천 이유와 함께 추천합니다.</p>
          <textarea
            value={introText}
            onChange={(e) => setIntroText(e.target.value)}
            rows={3}
            placeholder="예: 등산과 여행을 좋아하고 유머있으며 신앙심 깊은 50대 남성 / 요리를 즐기는 따뜻한 성격"
            className="w-full bg-stone-50 border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-stone-400 resize-y"
            data-testid="ai-intro-textarea"
          />
          {aiError && <p className="text-xs text-red-500 mt-1.5" data-testid="ai-error">{aiError}</p>}
          {aiKeywords.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="ai-keywords">
              <span className="text-xs text-stone-400">AI 검색 키워드:</span>
              {aiKeywords.map((kw) => (
                <span key={kw} className="bg-stone-100 text-stone-700 px-2.5 py-1 rounded-full text-xs font-medium">{kw}</span>
              ))}
            </div>
          )}
          <Button
            onClick={handleAiRecommend}
            disabled={isAiLoading || introText.trim().length < 2}
            className="mt-3 w-full sm:w-auto flex items-center justify-center gap-2 px-6 bg-stone-800 hover:bg-stone-900 text-white"
            data-testid="ai-recommend-btn"
          >
            <Sparkles className="w-4 h-4" />
            {isAiLoading ? "AI 검색 중..." : "AI 추천받기"}
          </Button>
        </div>
        <div className="flex gap-6">
          {/* List */}
          <div className="flex-1">
            {results === null ? (
              <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
                <Search className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-400 text-sm">키워드를 입력하고 검색하세요</p>
                <p className="text-stone-300 text-xs mt-1">예: 50대, 서울, 기독교, 등산, ENFP</p>
              </div>
            ) : isSearching ? (
              <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
                <p className="text-stone-400">검색중...</p>
              </div>
            ) : results.length === 0 ? (
              <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
                <p className="text-stone-400">검색 결과가 없습니다</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-stone-500 mb-2">{results.length}명의 회원이 검색되었습니다</p>
                {results.map((m) => {
                  const age = getAge(m.birth_year);
                  const isSelected = selectedMember?.id === m.id;
                  return (
                    <div
                      key={m.id}
                      onClick={() => setSelectedMember(m)}
                      className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${
                        isSelected ? "border-burgundy-400 ring-2 ring-burgundy-100" : "border-stone-200 hover:border-stone-300"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-full bg-stone-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {m.profile_image ? (
                            <img src={m.profile_image} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User className={`w-5 h-5 ${m.gender === "female" ? "text-pink-400" : "text-blue-400"}`} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-bold text-sm text-stone-800">{m.username}</span>
                            <MembershipBadge membership={m.membership_type} size="sm" />
                            <DocumentVerifiedBadge verified={m.document_verified} size="sm" />
                            <span className="text-xs text-stone-400">{m.gender === "male" ? "남" : "여"}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 text-xs text-stone-500">
                            {age && <span>{age}세 ({m.birth_year})</span>}
                            {(m.location || m.city) && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{m.location || m.city}</span>}
                            {m.job && <span className="flex items-center gap-0.5"><Briefcase className="w-3 h-3" />{m.job}</span>}
                          </div>
                        </div>
                        <Eye className="w-4 h-4 text-stone-300" />
                      </div>
                      {m.match_reason && (
                        <div className="mt-2 flex items-start gap-1.5 bg-stone-50 border border-stone-200 rounded-lg px-2.5 py-1.5" data-testid={`ai-reason-${m.id}`}>
                          <Sparkles className="w-3 h-3 text-burgundy-500 mt-0.5 flex-shrink-0" />
                          <span className="text-xs text-stone-600 leading-snug">{m.match_reason}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Detail Panel */}
          {selectedMember && (
            <div className="w-80 flex-shrink-0">
              <div className="bg-white rounded-2xl border border-stone-200 sticky top-24 overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-burgundy-500 to-burgundy-600 p-5 text-white text-center">
                  <div className="w-16 h-16 rounded-full bg-white/20 mx-auto mb-2 overflow-hidden flex items-center justify-center">
                    {selectedMember.profile_image ? (
                      <img src={selectedMember.profile_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-7 h-7 text-white/80" />
                    )}
                  </div>
                  <p className="font-bold">{selectedMember.username}</p>
                  <p className="text-burgundy-200 text-xs mt-0.5">
                    {selectedMember.gender === "male" ? "남" : "여"} · {getAge(selectedMember.birth_year)}세 · {selectedMember.location || selectedMember.city || ""}
                  </p>
                </div>

                {/* Info */}
                <div className="p-4 space-y-3 max-h-[50vh] overflow-y-auto text-xs">
                  <InfoBlock title="기본정보">
                    <InfoRow label="이메일" value={selectedMember.email} />
                    <InfoRow label="직업" value={selectedMember.job} />
                    <InfoRow label="MBTI" value={selectedMember.mbti} />
                    <InfoRow label="싱글여부" value={selectedMember.marital_status} />
                    <InfoRow label="이혼/사별 경과기간" value={selectedMember.single_years} />
                  </InfoBlock>
                  <InfoBlock title="외모/신체">
                    <InfoRow label="키" value={selectedMember.height} />
                    <InfoRow label="몸무게" value={selectedMember.weight} />
                    <InfoRow label="혈액형" value={selectedMember.blood_type} />
                    <InfoRow label="스타일" value={selectedMember.appearance_style} />
                  </InfoBlock>
                  <InfoBlock title="학력/직업">
                    <InfoRow label="학력" value={selectedMember.education} />
                    <InfoRow label="학교" value={selectedMember.school_name} />
                    <InfoRow label="회사" value={selectedMember.company_name} />
                  </InfoBlock>
                  <InfoBlock title="성격/생활">
                    <InfoRow label="종교" value={selectedMember.religion} />
                    <InfoRow label="음주" value={selectedMember.drinking} />
                    <InfoRow label="흡연" value={selectedMember.smoking} />
                    {selectedMember.personality?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedMember.personality.map((p, i) => (
                          <span key={i} className="bg-burgundy-50 text-burgundy-600 px-1.5 py-0.5 rounded text-[10px]">#{p}</span>
                        ))}
                      </div>
                    )}
                  </InfoBlock>
                  <InfoBlock title="취미/관심사">
                    <InfoRow label="취미" value={selectedMember.hobbies} />
                    <InfoRow label="여가활동" value={selectedMember.leisure_activities} />
                    <InfoRow label="좋아하는 음식" value={selectedMember.favorite_food} />
                  </InfoBlock>
                  <InfoBlock title="가치관">
                    <InfoRow label="좌우명" value={selectedMember.life_motto} />
                    <InfoRow label="중요한 가치" value={selectedMember.important_values} />
                    <InfoRow label="10년 뒤" value={selectedMember.future_vision} />
                  </InfoBlock>
                  <InfoBlock title="이상형/결혼관" accent>
                    <InfoRow label="이상형" value={selectedMember.ideal_type} />
                    <InfoRow label="결혼 목표" value={selectedMember.marriage_goal} />
                  </InfoBlock>
                  {selectedMember.introduction && (
                    <InfoBlock title="자기소개">
                      <p className="text-stone-600 whitespace-pre-line leading-relaxed">{selectedMember.introduction}</p>
                    </InfoBlock>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoBlock({ title, children, accent }) {
  return (
    <div className={`rounded-lg p-2.5 ${accent ? "bg-burgundy-50 border border-burgundy-100" : "bg-stone-50"}`}>
      <p className={`text-[10px] font-semibold mb-1.5 ${accent ? "text-burgundy-400" : "text-stone-400"}`}>{title}</p>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between py-0.5">
      <span className="text-stone-400">{label}</span>
      <span className="text-stone-700 font-medium text-right max-w-[60%]">{value}</span>
    </div>
  );
}
